import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { updateAdditionalExpenses } from '../services/tagService'
import type { Tag } from '../types/tag'
import { DurationSelector } from './DurationSelector'
import Input from './Input'
import SubmitButton from './SubmitButton'

interface AdditionalExpensesFormProps {
  tagId: string
  tag?: Tag
  onUpdate?: () => void
}

const AdditionalExpensesForm: React.FC<AdditionalExpensesFormProps> = ({
  tagId,
  tag,
  onUpdate,
}) => {
  const [tolls, setTolls] = useState('')
  const [homeTravelTime, setHomeTravelTime] = useState('')
  const [isDirty, setIsDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (tag) {
      setTolls(tag.additional_expenses?.tolls?.toString() || '')
      setHomeTravelTime(tag.additional_expenses?.homeTime?.toString() || '')
      setIsDirty(false)
    }
  }, [tag])

  const validateNumber = (value: string): boolean => {
    if (!value) return true
    const num = Number.parseFloat(value)
    return !Number.isNaN(num) && num >= 0
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      const updates = {
        tolls,
        homeTime: homeTravelTime,
        yardTime: '0', // We no longer use yard time
        notes: tag?.notes || '',
      }

      await updateAdditionalExpenses(tagId, updates)
      setIsDirty(false)
      toast.success('Changes saved successfully')
      onUpdate?.()
    } catch (error) {
      console.error('Failed to update additional expenses:', error)
      toast.error('Failed to save changes. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className={css(styles.formSection)}>
      <div className={css(styles.formContent)}>
        <div className={css(styles.inputGroup)}>
          <label className={css(styles.label)}>Tolls</label>
          <div className={css(styles.inputWrapper)}>
            <span className={css(styles.dollarSign)}>$</span>
            <Input
              value={tolls}
              onChange={(e) => {
                const newValue = e.target.value
                if (validateNumber(newValue)) {
                  setTolls(newValue)
                  setIsDirty(true)
                }
              }}
              type="number"
              step="0.01"
              min="0"
              className={css(styles.tollsInput)}
              disabled={isSaving}
            />
          </div>
        </div>

        <div className={css(styles.inputGroup)}>
          <label className={css(styles.label)}>Travel Time</label>
          <DurationSelector
            value={homeTravelTime}
            onChange={(value) => {
              setHomeTravelTime(value)
              setIsDirty(true)
            }}
            disabled={isSaving}
          />
        </div>

        <div className={css(styles.buttonContainer)}>
          <SubmitButton
            text={isSaving ? 'Saving...' : 'Save Changes'}
            onClick={handleSave}
            canSubmit={isDirty && !isSaving}
          />
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  formSection: {
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
  },
  formContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  inputGroup: {
    marginBottom: '16px',
    width: '100%',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '8px',
    display: 'block',
  },
  inputWrapper: {
    position: 'relative',
  },
  dollarSign: {
    position: 'absolute',
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#374151',
    fontSize: '14px',
    pointerEvents: 'none',
    zIndex: 1,
  },
  tollsInput: {
    paddingLeft: '24px !important',
    height: '48px',
    backgroundColor: '#F9FAFB',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
})

export default AdditionalExpensesForm
